// src/components/common/SearchBereichWahl.jsx
/**
 * SearchBereichWahl — die Bereichsauswahl der globalen Suche (Stufe B).
 *
 * @description
 *   Ein Auswahlfeld, das links IN der Suchpille sitzt („All" plus je
 *   Suchbereich eine Zeile). Es traegt den einen Backend-Parameter `types=`,
 *   der die Treffer auf den gewaehlten Bereich verengt.
 *
 *   🔴 **Die Liste kommt vom Server, nicht aus einer zweiten Tabelle.** Der
 *   Kode holt sie einmal je Seitenaufruf von `GET /api/search/bereiche`, und
 *   der **Syntax-Parser im Backend-Rumpf** liest dieselbe Tabelle
 *   (`backend/search_bereiche.py`). Waere die Liste hier noch einmal
 *   hingeschrieben, gaebe es zwei Vokabeln — und eine davon waere irgendwann
 *   falsch. Deshalb traegt jede Zeile auch ihren **Kurzweg** (`product:`) als
 *   Hinweis: er stammt aus derselben Antwort, die der Parser liest.
 *
 *   🔴 **Die Auswahl ist „All" oder EIN Bereich.** Die Syntax (`PO: 3822`) kann
 *   nur einen Bereich meinen, und ein Mehrfachfeld in einer 35-Zeichen-Pille
 *   waere nicht bedienbar. `types` bleibt trotzdem eine Liste — das ist die Form
 *   des Endpunkts, nicht die der Oberflaeche.
 *
 *   🔴 **Eine abgeschaltete Art steht GESPERRT im Menue, mit Grund** (Regel-16-
 *   Befund B2, Nachbesserung 1). Der Server liefert je Art `aktiv` — den
 *   lebenden Schalterzustand aus demselben Leser wie die Suche, nicht den
 *   Startwert `default_on`. Bis dahin bot das Menue `blog_topic` an, obwohl die
 *   Art per Vorgabe abgeschaltet ist: die Wahl schickte `types=['blog_topic']`,
 *   die wirksame Menge war leer, und der Nutzer sah „Nothing matches" — eine
 *   tote Option in der Auslieferungskonfiguration, und jeder Admin-Umgriff
 *   konnte weitere toeten.
 *
 *   🔴 **Gesperrt und benannt, nicht ausgeblendet.** Ausblenden waere die
 *   naheliegende und falsche Abhilfe (die Art verschwaende spurlos, samt der
 *   Auskunft, dass es sie gibt). Die Zeile traegt `disabled`, den sichtbaren
 *   Zusatz „Off in Admin settings" und ihn im zugaenglichen Namen — und der
 *   `onChange`-Riegel verwirft die Kennung trotzdem, weil MUI den Klick auf eine
 *   `disabled`-Option durchreichen kann (gemessen an der Hinweiszeile, 2026-10-15).
 *
 *   🔴 **Der Zustand wird beim OEFFNEN frisch geholt** (`onOpen` →
 *   `ladeBereiche({ frisch: true })`). Der Zwischenspeicher gilt bis zum
 *   Neuladen; ohne diesen Griff stuende nach einem Admin-Umgriff die falsche
 *   Aussage im Menue. Das ist die Grenze, die der Bericht benannt hat — sie ist
 *   damit nicht aufgehoben, sondern auf „so alt wie das offene Menue" verkuerzt.
 *
 *   🔴 **Fällt die Liste aus, bleibt das Feld bedienbar.** Es zeigt dann nur
 *   „All", und „All" heisst „kein Filter" — also genau das Verhalten vor Stufe B.
 *   Ein Ausfall darf die Suche nicht verengen (Regel 22 §4: „unbekannt" ist nie
 *   eine stille 0).
 *
 *   🔴 **Die getippte Syntax ueberstimmt die Auswahl — und das steht in der
 *   Pille.** `PO: 3822` schlaegt das Dropdown, geparst wird **serverseitig**
 *   (B6). Die Oberflaeche erfaehrt das Ergebnis ueber die Antwort-Koepfe
 *   `X-Search-Types` / `X-Search-Q` (`backend/search.py`) und zeigt den
 *   **wirksamen** Bereich, nicht die Auswahl — sonst behauptete die Pille „All",
 *   waehrend Bestellungen kommen (R16-B2, „Anzeige gegen die Messung"). Die
 *   Auswahl bleibt dabei der Zustand fuer den naechsten Freitext; dass sie
 *   gerade nicht gilt, sagt der Kurzweg in Klammern (`Purchase Order (po:)`).
 *   **Kein zweiter Parser** — angezeigt wird das Ergebnis desselben Parsers, der
 *   auch filtert.
 *
 *   🔴 **Der Kurzweg haengt am SERVER-SIGNAL, nicht am Unterschied** (R16-W1).
 *   „Der wirksame Bereich weicht von der Auswahl ab" hat zwei Ursachen: die
 *   Syntax hat gegriffen — oder die Messung ist veraltet, weil die AUSWAHL gerade
 *   umgestellt wurde. Nur die erste rechtfertigt den Beleg; im zweiten Fall
 *   stuende nach einem Bereichswechsel `Product (product:)` in der Pille, obwohl
 *   niemand je ein Token getippt hat. Der Aufrufer meldet die erste Ursache
 *   deshalb getrennt: `syntaxWirksam` ist `wirksam.text !== wirksam.fuer` — genau
 *   „der Server hat meinen Text angetastet", und `X-Search-Q` wird nur dann
 *   gesetzt.
 *
 *   🔴 **Ist die Syntax wirksam, sagt es das offene Menue** (R16-W3): eine nicht
 *   waehlbare Hinweiszeile, weil eine Auswahl dort sonst nichts Sichtbares
 *   bewirkte — die Pille folgt weiter dem wirksamen Bereich. Die Auswahl selbst
 *   aendert sich nicht; der Rueckweg ist „Text leeren" und steht im Wortlaut.
 *
 *   🔴 **Der Kurzweg-Zusatz traegt keine Abschwaechung mehr** (R16-B1, zweite
 *   Haelfte): `color: 'inherit'` macht ihn modusfest, und weil er jetzt Farbe UND
 *   Deckkraft des Etiketts daneben traegt, kann sein Kontrast nicht mehr unter
 *   den des Etiketts fallen (hell 5,17:1 fokussiert / 6,89:1 ruhend, dunkel
 *   ≥ 5,7:1 — Rechnung am Kode). `opacity: 0.75` ergab hell fokussiert 3,71:1.
 *
 *   🔴 **Die waehlbaren Menuezeilen tragen den Zeigerboden** (`MENUEZEILE_SX`):
 *   44 px auf grobem Zeiger, sonst die natuerliche Hoehe (30,75 px, ueber der
 *   AA-Norm von 24). `MenuItem` ist ein Nicht-MUI-Knopf und bekommt den Boden
 *   nicht aus dem Theme — die Hausform ist der Spread, nicht eine zweite Zahl.
 *
 *   🔴 **Und das ist GEMESSEN, nicht angenommen** (2026-10-16, A/B der Bahn):
 *   `theme.js` traegt **keinen** `MuiMenuItem`-Eintrag, und MUI's eigene
 *   `MenuItem`-Vorgaben sind `min-height: 48` bzw. ab 600 px `auto`
 *   (`MenuItem.js:66/137`) — **kein** `(pointer: coarse)`-Block. Die eine Regel
 *   `@media (pointer: coarse) { min-height: 44px }`, die die Messung an der
 *   Zeilenklasse findet, ist damit UNSERE. Nimmt man `...TOUCH_TARGET_COARSE`
 *   heraus, faellt die Zeile auf 30,75 px (6 + 18,75 + 6) — die Mutation traegt.
 *   ⚠️ Unter 600 px Breite bliebe sie bei MUI's 48 px gruen; deshalb misst
 *   `bereich.spec.ts` B.7b bei 1024 px, wo MUI's Block `auto` sagt.
 *
 *   🔴 **Etikett und Pfeil beruehrten sich** (R15 W-3, gemessen 2026-10-16):
 *   `luecke = −1 px`. Den Platz traegt `SUCHBEREICH_PFEILRAUM_SX` — und der
 *   Grund, warum es ihn braucht, ist die Spezifitaetsfalle am
 *   `.MuiSelect-select` (s. u., dort zweimal).
 *
 *   🔴 **Die mobile Form ist ein TOGGLE mit Roving-Tabindex** (R16-W1/W5,
 *   Nachbesserung 2). Der Knopf traegt `aria-expanded`; der zweite Tipp
 *   schliesst das Blatt (`beimOeffnenMobil` togglet). Im Blatt ist genau EINE
 *   Zeile Tab-Stopp (`tabIndex=0`), alle anderen `-1`; Pfeile/Home/End
 *   verschieben den Stopp mit dem Fokus, Tab verlaesst die Liste. Eine
 *   gesperrte Zeile traegt `cursor: default` (R16-W9), die Hinweiszeile ist
 *   `role="note"` und keine Option (R16-W12) — und seit R15-W2/R16-W17 ein
 *   GESCHWISTER VOR der Listbox, per `aria-describedby` mit ihr verknuepft,
 *   weil ARIA einer Listbox nur `option`/`group` als Kinder erlaubt. Der
 *   zugängliche Name des
 *   Ausloesers nennt den Bereich: `Search area: <Etikett>` (R16-W2).
 *
 *   🔴 **Der Toggle holt den Fokus ins EINGABEFELD — bewusst, kein APG-Verstoss
 *   im Sinne des Hauses** (R16-W13, Nachbesserung 2). Ein APG-Disclosure liesse
 *   den Fokus auf dem Ausloeser; hier ist die Tastatur das Ziel, und der
 *   Rueckweg „tippen → tippen → weitertippen" ist kuerzer. `M3.9d` pinnt beide
 *   Richtungen (oeffnen und schliessen), damit die Entscheidung nicht
 *   stillschweigend kippt.
 *
 * @useWhen
 *   - Eine globale Suche auf einen Inhaltsbereich verengen soll
 *   - Neben der Eingabe ein entdeckbares Gegenstueck zur Syntax gebraucht wird
 *   - Die Bereichswahl MOBIL als Blatt gebraucht wird (`< 600 px`): derselbe
 *     Baustein rendert dann einen echten Knopf mit `aria-haspopup="listbox"`
 *     und ein Blatt ueber der Kapsel — die Suche bleibt dabei offen
 *
 * @dontUseFor
 *   - Mehrfachauswahl ueber viele Facetten — dafuer `BottomSheetMultiSelect`
 *   - Facettenfilter einer Liste — dafuer `FilterPillRow` / `FilterSheet`
 *   - Eine modale Auswahl, die die Suche SCHLIESSEN soll — das Blatt hier
 *     laesst sie ausdruecklich offen (§B2.7). `BottomSheetSelect` ist ein
 *     modaler `SwipeableDrawer` am LAYOUT-Viewport (hinter der Tastatur) und
 *     widerspricht genau dieser Bedingung; `ActionSheet` ist ein
 *     Formularbehaelter mit Anfasser und CTA, keine Auswahlliste.
 *
 * @related
 *   - SearchResultItem — beschriftet eine TREFFERZEILE (`KIND_LABEL`); die
 *     Bereiche hier tragen dieselben Woerter, sind aber die Auswahlliste
 *   - SearchTrigger — das Suchfeld der Listenseiten, ohne Bereichswahl
 *   - BottomSheetSelect — mobil ein modaler Drawer; NICHT fuer dieses Feld
 *     (die Suche muss offen bleiben und der Fokus ins Eingabefeld zurueck)
 *
 * @props
 *   wert          — der gewaehlte `kind` oder `''` fuer „All" (der ZUSTAND)
 *   wirksam       — der vom Server gemeldete wirksame `kind`; weicht er von
 *                   `wert` ab, zeigt die Pille IHN
 *   syntaxWirksam — `true`, wenn der SERVER den Text angetastet hat
 *                   (`wirksam.text !== wirksam.fuer`). Nur dann erscheint der
 *                   Kurzweg als Beleg und die Hinweiszeile im Menue; der blosse
 *                   Unterschied Auswahl ↔ wirksamer Bereich genuegt NICHT
 *                   (R16-W1 — sonst zeigt die Pille eine nie getippte Syntax)
 *   onChange      — `(kind) => void`; `''` heisst „Filter weg"
 *   ohneAusloeser — `true` rendert NUR das Blatt (Suche mobil v2, Stufe 2):
 *                   geoeffnet wird es ueber „All filters" der Bereichsleiste
 *   blattOffen    — offener Zustand GESTEUERT; `undefined` = intern
 *   onBlattOffenAendern — `(offen) => void`, meldet jede Aenderung
 *   blattObenPx   — die Unterkante des Bereichs, unter dem das Blatt
 *                   aufklappt (Vorgabe: Unterkante der Feldzeile).
 *                   🔴 Der DESKTOP-Zweig liest keine dieser Props — er rendert
 *                   eine `Select` mit Menue und erreicht die Blattgeometrie nicht
 *   onGeoeffnet   — `() => void`, laeuft BEIM OEFFNEN des Menues (MUI's
 *                   `onOpen`). Der Kopf setzt daran seine Marke
 *                   `bereichsMenueOffen`: solange das Menue offen ist, darf
 *                   ein Klick daneben die Kopfsuche nicht zusammenfallen
 *   onGeschlossen — `() => void`, laeuft NACHDEM das Menue zu ist (MUI's
 *                   `onExited`) — UND beim Abhaengen der Komponente, wenn das
 *                   Menue noch offen war (Aufraeumer, R15-W1). Der Kopf loescht
 *                   daran dieselbe Marke und holt den Fokus ins Suchfeld
 *                   zurueck (Owner-Befund 2026-10-16).
 *                   🔴 **Mobil laeuft er SYNCHRON** in `schliesseBlatt` (das
 *                   Blatt ist keine Transition, es gibt kein `onExited`);
 *                   Desktop erst am `onExited` der Grow-Transition. Der
 *                   Aufrufer darf sich deshalb nicht auf „nach dem naechsten
 *                   Frame" verlassen — `M3.9d` misst den Fokus nach dem
 *                   Schliessen in beiden Formen.
 *                   🔴 Die Komponente hat den Fokus VORHER schon selbst auf den
 *                   Ausloeser gesetzt: MUI's eigene Rueckgabe ist per
 *                   `disableRestoreFocus` abgeschaltet. 🔴 **Dass sie NACH
 *                   diesem Rueckruf faellt, ist NICHT nachgelesen — Hypothese**
 *                   (R16-B3); Rotweg s7: Entfernen GRUEN (M-S-4), also
 *                   Absicherung, nicht Traeger. Der letzte `focus()` gewinnt
 *   sx            — zusaetzliche Stile des Aufrufers
 *
 *   🔴 **Der Mobilzweig ist KEIN Prop.** Er folgt aus
 *   `useMediaQuery(theme.breakpoints.down('sm'))` — derselben Grenze, an der
 *   `SidebarAndHeaderBar` den Mobil-Zweig rendert (§B2.10: keine zweite
 *   Breitenabfrage). Unter 600 px rendert der Baustein ein Blatt (`role=
 *   "listbox"`); ab 600 px die MUI-`Select`-Klappliste mit `role="combobox"`.
 *   🔴 **Den mobilen AUSLOESER-KNOPF gibt es seit Suche mobil v2 Stufe 2 nicht
 *   mehr** (Plan §9.1, W14): das Blatt wird ueber „All filters" der
 *   Bereichsleiste geoeffnet (`ohneAusloeser`), und der Aufrufer reicht die
 *   Blattgeometrie als Zahl herein (`blattObenPx`). Die frueher hier
 *   beschriebene Prop `kompakt` ist mit ihm gefallen (R16-W3/R15-W3).
 */

import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import axios from 'axios'
import { Select, MenuItem, Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import CheckIcon from '@mui/icons-material/Check'
import {
  FS, MOBILE_SEARCH_GLASS_SX, SUCHBEREICH_BLATT_ZEILE_PX,
  SUCHBEREICH_MIN_HEIGHT_SX, SUCHBEREICH_PFEILRAUM_SX,
  SUCHE_ABSTAND_PX, SUCHE_KARTE_RADIUS, SUCHE_V2_RAND,
  SUCHE_FELD_HOEHE_PX, TOUCH_TARGET_COARSE,
} from '../../theme/tokens'

const API = process.env.REACT_APP_API_BASE_URL

/** Der Wert fuer „kein Bereich" — der leere String, nicht `null`. */
export const ALLE_BEREICHE = ''

// 🔴 Ein Zwischenspeicher je Seitenaufruf, kein zweiter Aufruf je Suchfeld.
// Kopfzeile und mobiles Panel rendern dasselbe Feld; ohne ihn holte jedes
// Oeffnen des Mobil-Panels die Liste erneut.
//
// 🔴 **Er gilt bis zum NAECHSTEN LADEN — und die Antwort ist seit der
// Nachbesserung 1 nicht mehr statisch.** Sie traegt je Art `aktiv` (den lebenden
// Schalterzustand, Regel-16-Befund B2). Ein Admin-Umgriff ist damit bis zum
// Neuladen der Seite veraltet: die Zeile stuende weiter „waehlbar", obwohl die
// Art abgeschaltet ist. Deshalb holt `SearchBereichWahl` die Liste beim OEFFNEN
// des Menues frisch (`ladeBereiche({ frisch: true })`) — EINE Abfrage je
// Oeffnen, und der Zustand ist so alt wie das offene Menue.
let _zwischenspeicher = null
let _laufend = null

/**
 * Holt die Bereichs-Tabelle einmal und teilt sie allen Aufrufern.
 *
 * 🔴 Der Fehlerfall liefert eine **leere Liste**, keinen Wurf: der Aufrufer
 * zeigt dann nur „All", und „All" ist das Verhalten ohne Stufe B. Ein Wurf
 * machte die ganze Suchpille unbrauchbar, weil eine Nebenliste fehlt.
 *
 * `frisch: true` wirft den Zwischenspeicher weg und holt neu — die eine Stelle,
 * an der ein Aufrufer den Zustand altern lassen darf. Waehrend ein Abruf laeuft,
 * teilen sich weitere Aufrufer denselben (`_laufend`).
 */
export function ladeBereiche({ frisch = false } = {}) {
  if (frisch) {
    _zwischenspeicher = null
    _laufend = null
  }
  if (_zwischenspeicher) return Promise.resolve(_zwischenspeicher)
  if (!_laufend) {
    _laufend = axios
      .get(`${API}/api/search/bereiche`)
      .then(({ data }) => {
        _zwischenspeicher = Array.isArray(data) ? data : []
        return _zwischenspeicher
      })
      .catch(() => {
        _laufend = null // der naechste Aufruf darf es erneut versuchen
        return []
      })
  }
  return _laufend
}

/** Nur fuer Proben: den Zwischenspeicher leeren. */
export function setzeBereicheZurueck() {
  _zwischenspeicher = null
  _laufend = null
}

/**
 * Das Etikett eines Bereichs — die EINE Stelle, an der ein `kind` zu einem Wort
 * wird.
 *
 * 🔴 **Auch fuer den Aufrufer**, nicht nur fuer dieses Feld: der Leerzustand der
 * Suche nennt den wirksamen Bereich („Nothing matches „3822"." mit der zweiten
 * Zeile „Searched in: Purchase Order"), und er darf dafuer keine zweite Tabelle
 * aufmachen. Die Liste kommt aus derselben Server-Antwort
 * (`GET /api/search/bereiche`) wie Parser und Menue.
 *
 * 🔴 **Unbekannt heisst NICHT „All".** Ist die Kennung nicht in der Liste (die
 * Liste laedt noch, oder sie ist ausgefallen), kommt die **rohe Kennung**
 * zurueck. „All" waere eine Anzeige gegen die Messung — die Abfrage filtert ja
 * weiterhin auf diese Kennung.
 */
export function bereichsEtikett(kind, liste) {
  if (!kind) return 'All'
  const quelle = liste || _zwischenspeicher || []
  const treffer = quelle.find((b) => b.kind === kind)
  return treffer ? treffer.label : kind
}

/** Der Kurzweg (`po`) eines Bereichs — aus derselben Antwort wie das Etikett. */
function kurzwegVon(kind, liste) {
  const treffer = liste.find((b) => b.kind === kind)
  return treffer?.aliases?.[0] || null
}

/**
 * Der sichtbare Zusatz der gesperrten Zeile — und der Grund im zugaenglichen
 * Namen (Regel-16-Befund B2, Nachbesserung 1).
 *
 * 🔴 **Warum ENGLISCH.** Die Flaeche ist englisch (`All`, `Purchase Order`,
 * `The area comes from your typed prefix …`), und der Grund verweist auf eine
 * englische Oberflaeche („Admin settings"). Ein deutscher Halbsatz waere die
 * zweite Sprache in derselben Zeile — und der Sprachwaechter
 * (`scripts/check_german_words.sh`) ist ein Ratenschalter, der nur sinken darf.
 *
 * 🔴 **Warum er NICHT `aria-hidden` ist**, anders als der Kurzweg daneben
 * (`aria-hidden="true"`, weil er die ANZEIGE-Hilfe ist): hier ist der Zusatz die
 * ganze Aussage. Ohne ihn heisst die Zeile fuer einen Screenreader „Blog Topic,
 * nicht waehlbar" — und der Nutzer erfaehrt nirgends, WOHIN er gehen muss.
 */
const ABGESCHALTET = 'Off in Admin settings'

/**
 * Ist diese Art JETZT abgeschaltet? — fuer die Suchflaeche, die den Leerzustand
 * begruenden muss (`SidebarAndHeaderBar.jsx`).
 *
 * 🔴 **Dieselbe Quelle wie das Menue** (`_zwischenspeicher`), also dieselbe
 * Antwort, die auch `bereichsEtikett` liest. Eine zweite Abfrage hier waere ein
 * zweiter Zustand, und die zwei koennten auseinanderlaufen.
 *
 * 🔴 **Unbekannt heisst NICHT „abgeschaltet".** Ist die Kennung nicht in der
 * Liste — sie laedt noch, oder sie ist ausgefallen —, kommt `false` zurueck.
 * Die Gegenrichtung waere eine Behauptung ueber einen Zustand, den niemand
 * gemessen hat (Regel 22 §5: „unbekannt" ist nie ein stiller Wert).
 *
 * 🔴 **Der Zwischenspeicher ist kein Abonnement** (R15-W1). Gelesen wird zur
 * RENDERZEIT, ohne Benachrichtigung: ein Admin-Umgriff, der den Zwischenspeicher
 * frisch macht (Menue geoeffnet), rendert die Suchflaeche NICHT neu. Der
 * Leerzustand kann deshalb um EINEN Render nachhaengen. Schmal und benannt: der
 * Zweig ist nur ueber die getippte Syntax erreichbar (`blog: kaffee`), und
 * Tippen rendert ohnehin neu. Kein Defekt — aber die Grenze gehoert hierher und
 * nicht in einen Bericht.
 */
export function bereichIstAbgeschaltet(kind) {
  if (!kind) return false
  const quelle = _zwischenspeicher || []
  const treffer = quelle.find((b) => b.kind === kind)
  return treffer ? treffer.aktiv === false : false
}

/**
 * Die Kennung der Hinweiszeile im Menue — **kein Suchbereich**, und das ist der
 * Punkt: sie kann in keiner Antwort der Sicht vorkommen.
 *
 * 🔴 **Warum es sie ueberhaupt braucht (gemessen 2026-10-15).** MUI klont JEDES
 * Kind eines `Select` zur Option: `SelectInput.js` haengt ihm `role="option"`,
 * `data-value` und **einen eigenen `onClick`** an
 * (`onClick: handleItemClick(child)` — er ERSETZT einen eigenen Handler). Ein
 * `disabled` allein haelt den Klick deshalb nicht ab: die Probe
 * „W3 — … nicht waehlbare Hinweiszeile" war mit `disabled` allein rot, weil
 * `onChange('__syntax-hinweis__')` sehr wohl gefeuert wurde. Der zweite, tragende
 * Riegel ist der Wert: `onChange` reicht genau diese Kennung nicht weiter.
 */
const SYNTAX_HINWEIS = '__syntax-hinweis__'

/**
 * Der SATZ der Hinweiszeile — „die getippte Syntax gewinnt" (R16-W3).
 *
 * 🔴 **EINE Quelle, drei Verbraucher** (R16-C1, Nachbesserung 4, 2026-11-01).
 * Das Blatt zeigt ihn an zwei Stellen: als `role="note"` ueber der
 * Desktop-`Select` (`:970`) und als nicht waehlbare `MenuItem`-Zeile im mobilen
 * Blatt (`:1270`). Seit der Nachbesserung 4 traegt ihn auch die Chip-Leiste der
 * mobilen Suche (`SucheBereichChips`, Prop `syntaxHinweis`) — dort ist er die
 * einzige Erklaerung dafuer, dass ein Chip-Tipp nichts bewirkt. Drei
 * Abschriften desselben Satzes liefen beim naechsten Mikrocopy-Schritt
 * auseinander; deshalb steht er hier.
 *
 * ⚠️ Der Satz nennt URSACHE („comes from your typed prefix") und RUECKWEG
 * („clear the text") — die Hausform der Mikrocopy-Regel (R16-W3,
 * Orchestrator-Entscheid 2026-10-15). An der Chip-Leiste traegt „here" weiter:
 * die Zeile steht unmittelbar unter den Chips, also genau dort, wo man waehlen
 * wuerde.
 */
export const SYNTAX_HINWEIS_TEXT =
  'The area comes from your typed prefix — clear the text to choose here.'

/**
 * Der Zeigerboden der WAHLBAREN Menuezeilen.
 *
 * 🔴 **Warum er hier stehen muss und nicht aus dem Theme kommt (B.7, gemessen
 * 2026-10-15).** `MenuItem` ist ein **Nicht-MUI-Knopf** im Sinne der Hausregel:
 * `theme.js` traegt den 44er-Boden nur an `MuiButton`, `MuiIconButton`,
 * `MuiToggleButton` und den klickbaren Chips. Fuer alles andere —
 * `pattern-library.md` §Touch targets nennt `MenuItem` und `ListItem` beim Namen —
 * gilt: „dort `...TOUCH_TARGET_COARSE` spreaden, nie eine nackte 44."
 *
 * 🔴 **Und er haengt am ZEIGER, nicht an der Breite** (`pattern-library.md:1362-1373`):
 * der Nicht-MUI-Boden ist bewusst zeigerabhaengig. Ein schmales Fenster mit
 * FEINEM Zeiger (390 px im Desktop-Browser) behaelt die dichte Zeile — sie liegt
 * mit **30,75 px** ueber der AA-Norm von 24 px (WCAG 2.2, 2.5.8) und ist damit
 * kein Normverstoss. Genau deshalb misst `bereich.spec.ts` B.7 bei feinem Zeiger
 * **24** und B.7b auf grobem Zeiger **44**.
 *
 * ⚠️ Der Boden gilt fuer die WAHLBAREN Zeilen. Die Hinweiszeile
 * (`SYNTAX_HINWEIS`) ist kein Ziel — sie traegt `disabled` und wird nicht
 * angewaehlt; sie bekommt den Boden deshalb nicht.
 */
const MENUEZEILE_SX = { fontSize: FS.sm, ...TOUCH_TARGET_COARSE }

/** 🔴 M3b §B2.7 — die Zeile des mobilen Blatts. 48 px
 *  (`SUCHBEREICH_BLATT_ZEILE_PX`), ueber dem Hausboden 44; Etikett + Kurzweg +
 *  Grund + Haken. Kein MUI-Knopf, also auch kein geerbter Zeigerboden — die
 *  Hoehe steht hier. */
const BLATT_ZEILE_SX = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minHeight: SUCHBEREICH_BLATT_ZEILE_PX,
  px: 2,
  border: 'none',
  background: 'none',
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  textAlign: 'left',
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: -2,
  },
}

/**
 * Die HAUSFORM einer gesperrten Zeile, deren Text man LESEN koennen muss.
 *
 * 🔴 **Warum `'&.Mui-disabled'` und nicht `opacity: 1` daneben (R16-B2/B3).** Der
 * Unterschied ist die Spezifitaet: `opacity: 1` allein erzeugt `.css-x { … }` —
 * EINE Klasse (0,1,0) —, MUI's Vorgabe `&.Mui-disabled { opacity: 0.38 }` ist
 * `.css-x.Mui-disabled` — ZWEI Klassen (0,2,0). Sie gewinnt unabhaengig von der
 * Einfuegereihenfolge, und der Grund stuende bei ≈ 1,7:1.
 *
 * ⚠️ WCAG 1.4.3 nimmt INAKTIVE Bedienelemente aus — das ist kein Normverstoss,
 * sondern Wegfindung. Der Grund traegt hier aber die ganze Aussage („wohin muss
 * ich gehen"), und mit 0,38 verliert er sie.
 *
 * 🔴 **Sie steht hier, weil ZWEI Zeilen sie brauchen** (Auflage R16-B3): die
 * Syntax-Hinweiszeile und die Zeile einer abgeschalteten Art. Zwei Kopien
 * derselben Spezifitaetsfalle sind zwei Stellen, an denen sie wieder verloren
 * gehen kann.
 */
const LESBARE_SPERRE_SX = { '&.Mui-disabled': { opacity: 1 } }

export default function SearchBereichWahl({
  wert = ALLE_BEREICHE, wirksam, syntaxWirksam = false, onChange, sx,
  onGeoeffnet, onGeschlossen,
  // 🔴 Suche mobil v2 Stufe 2 (2026-11-01) — das Blatt OHNE Ausloeser. Der
  // mobile Aufrufer oeffnet es ueber den Chip „All filters" der Bereichsleiste
  // (`SucheBereichChips`), der Ausloeser-Knopf in der Feldzeile entfaellt
  // (Plan §9.1, W14). Der Desktop-Zweig liest die Prop nie — er rendert eine
  // `Select` mit Menue und erreicht die Blattgeometrie gar nicht.
  ohneAusloeser = false,
  // 🔴 Der offene Zustand kann GESTEUERT werden (dann kommt er vom Aufrufer,
  // der ihn fuer „All filters" ohnehin braucht) oder intern bleiben. `undefined`
  // heisst „intern" — der Desktop-Zweig und die Einzelproben setzen nichts.
  blattOffen: blattOffenGesteuert,
  onBlattOffenAendern,
  // 🔴 Der Anker des Blatts: die Unterkante des Bereichs, unter dem es
  // aufklappt. Bis Stufe 1 war das die Feldzeile (60 px). Seit die
  // Bereichsleiste dazwischenliegt, ist es ihre Unterkante + Polster — der
  // Aufrufer rechnet das aus SEINER Anordnung und reicht die Zahl herein; die
  // Komponente kennt die Chip-Zeile nicht. Vorgabe = Feldzeile ohne Chips.
  blattObenPx = SUCHE_V2_RAND * 8 + SUCHE_FELD_HOEHE_PX + SUCHE_V2_RAND * 8,
}) {
  // 🔴 M3b §B2.7/§B2.10 — EINE Breitengrenze im Haus: `theme.breakpoints.down('sm')`
  // (< 600 px). Dieselbe Grenze, an der `SidebarAndHeaderBar` den Mobil-Zweig
  // rendert. 🔴 **Die frueher hier erklaerte Prop `kompakt` ist gefallen**
  // (R16-W3/R15-W3, Nachbesserung 1, 2026-11-01): sie hatte keinen
  // Produktionsaufrufer mehr, seit der mobile Ausloeser-Knopf mit Stufe 2
  // entfallen ist („All filters" oeffnet das Blatt, `ohneAusloeser`). Sie
  // steuerte nur Schriftgroesse und Pfeilversatz — beides steht jetzt fest.
  const theme = useTheme()
  const istMobil = useMediaQuery(theme.breakpoints.down('sm'))
  const [bereiche, setBereiche] = useState(() => _zwischenspeicher || [])
  // 🔴 M3b — das mobile Blatt ist ein interner Teil DIESER Komponente (§B2.7):
  // kein neuer geteilter Baustein, ein Aufrufer. `blattOffen` ist der Zustand.
  //
  // 🔴 Stufe 2: er kann GESTEUERT sein (`blattOffenGesteuert !== undefined`).
  // Dann haelt ihn der Aufrufer — noetig, weil „All filters" in der
  // Bereichsleiste sitzt und derselbe Zustand dort `aria-expanded` traegt: zwei
  // Kopien waeren zwei Wahrheiten. Die Ref-Fuehrung ist dieselbe wie bei
  // `geschlossenCb`: der Rueckruf bleibt fuer die Lebensdauer der Instanz
  // derselbe, auch wenn der Aufrufer eine frische Pfeilfunktion hereinreicht.
  const [blattOffenIntern, setBlattOffenIntern] = useState(false)
  const aendernCb = useRef(onBlattOffenAendern)
  useEffect(() => { aendernCb.current = onBlattOffenAendern }, [onBlattOffenAendern])
  const gesteuertRef = useRef(false)
  gesteuertRef.current = blattOffenGesteuert !== undefined
  const blattOffen = gesteuertRef.current ? blattOffenGesteuert : blattOffenIntern
  const setBlattOffen = (naechster) => {
    if (!gesteuertRef.current) setBlattOffenIntern(naechster)
    aendernCb.current?.(naechster)
  }
  // 🔴 R16-W5 (Nachbesserung 2) — der ROVING-TABINDEX des Blatts: genau EINE
  // Zeile ist Tab-Stopp, alle anderen `-1`. Pfeile verschieben den Stopp mit
  // (APG „listbox popup"); Tab verlaesst die Liste. Ohne das liefen alle
  // Optionen in der Tab-Reihenfolge, und „folgt dem Listbox-Muster" waere zu
  // stark gewesen.
  const [blattFokus, setBlattFokus] = useState(0)
  // 🔴 R15-W2 / R16-W17 (Nachbesserung 3): die Hinweiszeile steht als
  // GESCHWISTER VOR der `role="listbox"`-Flaeche, nicht mehr als deren Kind —
  // ARIA erlaubt einer Listbox nur `option`/`group` als eigene Kinder. Die
  // Listbox zeigt per `aria-describedby` auf diese Kennung.
  const hinweisId = useId()
  // 🔴 R16-W18 (Nachbesserung 3): nur der ERSTE Lauf nach dem Oeffnen darf den
  // Fokus aus einem lebenden Element ausserhalb holen (er steht dann auf dem
  // Ausloeser). Jeder spaetere Lauf holt ihn nur zurueck, wenn er VERSCHWUNDEN
  // ist — sonst risse jede nachgeladene Antwort den Nutzer aus seinem Element.
  const erstOeffnenRef = useRef(false)

  useEffect(() => {
    let aktiv = true
    ladeBereiche().then((liste) => { if (aktiv) setBereiche(liste) })
    return () => { aktiv = false }
  }, [])

  // 🔴 **Der Zustand wird beim OEFFNEN frisch geholt** (Regel-16-Befund B2,
  // zweite Haelfte). Der Zwischenspeicher gilt bis zum Neuladen der Seite; ein
  // Admin-Umgriff waere im Menue sonst unsichtbar, und die Zeile stuende
  // weiter „waehlbar", obwohl die Art nichts mehr liefern kann. EINE Abfrage je
  // Oeffnen — und der Zustand ist damit so alt wie das offene Menue.
  //
  // Die Marke `montiert` haelt den Rueckruf davon ab, in eine abgeraeumte
  // Komponente zu schreiben; das Menue ist offen, aber die Seite kann sich
  // waehrend der Abfrage aendern (Navigation).
  // 🔴 **Die Marke wird im RUMPF gesetzt, nicht nur im Aufraeumer** (R15-B1,
  // gemessen 2026-10-16). `React.StrictMode` ist im DEV-Bau an
  // (`frontend/src/index.js`) und faehrt beim Montieren **Effekt → Aufraeumer →
  // Effekt**. Hatte der Effekt keinen Rumpf, setzte nur der Aufraeumer — die
  // Marke stand danach fuer die LEBENSDAUER der Instanz auf `false`, und der
  // frische Abruf beim Oeffnen schrieb nie wieder in den Zustand. Die Zeile
  // blieb gesperrt, obwohl der Admin sie gerade freigegeben hatte.
  //
  // Hausform: `vendors/VendorAnschriften.jsx`, `pages/invoicing/InvoiceDetail.jsx`.
  const montiert = useRef(true)
  useEffect(() => {
    montiert.current = true
    return () => { montiert.current = false }
  }, [])

  // 🔴 **Der `MenuProps`-Gegenstand muss STABIL sein — gemessen, nicht
  // vorsorglich.** Ein frisches Objekt je Render reicht MUI's Slot-Aufloesung
  // jedes Mal neue `TransitionProps` weiter; die `Grow`-Transition startet dann
  // bei jedem Render einen neuen Timer, und der Jest-Lauf mit `userEvent` kommt
  // nicht mehr zur Ruhe. Gemessen 2026-10-18: die ganze Kopf-Suite
  // (`SidebarAndHeaderBar.kopf-suchleerzustand.test.jsx`, 7 Faelle) lief in die
  // 30-s-Zeitlimits, sobald die Zeile ohne Memo dastand; dieselbe Suite ohne die
  // Zeile war gruen.
  //
  // 🔴 Und er haengt an einer REF, nicht an `onGeschlossen`: so bleibt er fuer
  // die Lebensdauer der Instanz derselbe, auch wenn ein Aufrufer eine frische
  // Pfeilfunktion hereinreicht. Ein `useMemo` mit `[onGeschlossen]` haette den
  // Haenger beim naechsten Aufrufer wieder eingebaut.
  const geschlossenCb = useRef(onGeschlossen)
  useEffect(() => { geschlossenCb.current = onGeschlossen }, [onGeschlossen])
  // 🔴 **Der Ausloeser, den MUI beim Schliessen zurueckholt — wir muessen ihn
  // selbst kennen** (S, Nachbesserung 4, 2026-10-18).
  //
  // Der Browserbefund: nach der Wahl stand der Fokus auf dem BEREICHSKNOPF,
  // obwohl `onGeschlossen` das Suchfeld fokussiert.
  //
  // `disableRestoreFocus` schaltet MUI's Rueckgabe ab; damit sie nicht
  // ersatzlos entfaellt (ein Tastaturnutzer, der ohne offene Suche einen
  // Bereich waehlt, stuende sonst auf `<body>`), holen WIR sie hier zurueck —
  // und rufen danach den Aufrufer, der sie fuer den Suchfall auf das
  // Eingabefeld umlenkt. Die Reihenfolge ist dieselbe wie vorher, nur mit uns
  // als Ausfuehrendem: Ausloeser → Aufrufer.
  //
  // 🔴 **WAS GEMESSEN IST — und was nicht** (R15-W3, S Nachbesserung 6,
  // 2026-10-18). Im Chromium-Rotweg s7 war die Mutation „`disableRestoreFocus`
  // entfernen" (M-S-4) **GRUEN**: der Fokus kehrte auch ohne die Zeile ins
  // Suchfeld zurueck, weil unser eigener `focus()` aus `onExited` der letzte
  // ist. Die Zeile ist damit eine **Absicherung** gegen eine MUI-Reihenfolge,
  // die wir nicht gemessen haben — nicht der Traeger des Fixes.
  //
  // ⚠️ Die MUI-Reihenfolge (der `Modal` gibt den Fokus nach dem `onExited` der
  // Grow-Transition zurueck) ist aus dem ausgelieferten Baum NICHT nachgelesen
  // (`node_modules` liegt ausserhalb des Arbeitsbaums und der Zugriff wurde
  // abgelehnt). Sie bleibt eine **HYPOTHESE**, gestuetzt auf das gemessene
  // Fokus-Endziel. Der Fix haengt nicht an ihr: unser `focus()` laeuft am
  // `onExited`, und der letzte `focus()` gewinnt.
  const wurzelRef = useRef(null)
  // 🔴 **Die Marke des Aufrufers darf die Komponente nicht ueberleben** (R15-W1,
  // S Nachbesserung 6, 2026-10-18). `onGeschlossen` haengt an `onExited` der
  // Grow-Transition; wird der Select WAEHREND des offenen Menues abgehaengt
  // (`showSearch: false`, `hasActions`), ruft React diese Transition nicht mehr,
  // und die Marke `bereichsMenueOffen` im Kopf bliebe fuer die Lebensdauer des
  // Kopfes stehen — jeder Klick daneben liefe danach ins Leere. Der Aufraeumer
  // loest sie ueber DENSELBEN Rueckruf. Kein Fokus-Sprung: `onGeschlossen` ruft
  // `inputRef.current?.focus()` zwar bei `fokusNachWahl`, aber beim Abhaengen ist
  // `inputRef.current` schon `null` (beide Wege haengen den GANZEN Kasten ab).
  const offenRef = useRef(false)
  useEffect(() => () => {
    if (offenRef.current) {
      offenRef.current = false
      geschlossenCb.current?.()
    }
  }, [])
  const menuProps = useMemo(
    () => ({
      disableRestoreFocus: true,
      TransitionProps: {
        onExited: () => {
          offenRef.current = false
          // Der fokussierbare Knoten des Select ist der Anzeige-`div` mit
          // `role="combobox"`, nicht die Wurzel (die Wurzel ist ein `div` ohne
          // Tab-Stopp). Der zweite Weg deckt den Fall, dass MUI den `ref` auf
          // das versteckte `input` weiterreicht statt auf die Wurzel — dann
          // findet `closest` den Select-Rahmen.
          const el = wurzelRef.current
          const knopf = el?.querySelector?.('[role="combobox"]')
            || el?.closest?.('.MuiSelect-root')?.querySelector('[role="combobox"]')
          knopf?.focus()
          geschlossenCb.current?.()
        },
      },
    }),
    [],
  )
  const beimOeffnen = () => {
    // 🔴 Die Marke des Aufrufers wird ZUERST gesetzt, vor dem Abruf (S,
    // 2026-10-18). Der Klick, der das Menue oeffnet, laeuft noch durch den
    // React-Baum, waehrend der Abruf unterwegs ist; ein `ClickAwayListener`
    // daneben sieht denselben Klick erst am Dokument. Wer die Marke hinter den
    // Abruf haengt, laesst genau dieses Fenster offen.
    offenRef.current = true
    onGeoeffnet?.()
    ladeBereiche({ frisch: true }).then((liste) => {
      if (montiert.current) setBereiche(liste)
    })
  }

  // ── M3b §B2.7 — das mobile Bereichsblatt ───────────────────────────────────
  //
  // Es VERTRITT die Trefferkarte an ihrer Stelle, legt sich nicht darueber und
  // geht nicht nach oben auf: die Obergrenze ist die Oberkante der Saeule, es
  // scrollt darunter selbst. `role="listbox"`, die Zeilen `role="option"`.
  const blattRef = useRef(null)
  const schliesseBlatt = () => {
    // 🔴 R15-W5 (Nachbesserung 1): die Marke des Aufrufers wird auch im
    // MOBILZWEIG zurueckgesetzt. Sie wird in `beimOeffnen` gesetzt; blieb sie
    // nach dem ersten Oeffnen stehen, rief der Unmount-Aufraeumer
    // `onGeschlossen` ein zweites Mal — beim heutigen Aufrufer folgenlos, bei
    // einem kuenftigen mit Nebenwirkung nicht.
    offenRef.current = false
    setBlattOffen(false)
    // Fokus zurueck ins Feld — ueber denselben Rueckruf wie beim Desktop. Der
    // Aufrufer loescht daran seine Marke und fokussiert die Eingabe (§B2.7).
    geschlossenCb.current?.()
  }
  // 🔴 R16-W1 (Nachbesserung 1): der Ausloeser ist ein TOGGLE. Vorher kehrte
  // `beimOeffnenMobil` bei offenem Blatt frueh zurueck — der zweite Tipp war ein
  // No-op, obwohl der Knopf `aria-expanded` traegt und ein Disclosure-Muster
  // damit einen Toggle verspricht. Die Probe `M3.9d` misst beide Richtungen.
  const beimOeffnenMobil = () => {
    if (blattOffen) { schliesseBlatt(); return }
    // 🔴 R16-W5: der Tab-Stopp steht beim Oeffnen auf der gewaehlten Zeile —
    // der Fokus-Effekt setzt ihn danach auf die tatsaechlich fokussierte.
    setBlattFokus(blattStart)
    // 🔴 R16-W18: der erste Lauf nach dem Oeffnen darf den Fokus vom Ausloeser
    // in die Liste holen; spaetere Laeufe nur, wenn er verschwunden ist.
    erstOeffnenRef.current = true
    setBlattOffen(true)
    // Derselbe Weg wie beim Desktop-Menue: Marke setzen, Liste frisch holen.
    beimOeffnen()
  }

  // 🔴 Die Marke des Aufrufers darf die Komponente nicht ueberleben — dieselbe
  // Lage wie beim Desktop-Menue (R15-W1): haengt der Aufrufer waehrend des
  // offenen Blatts ab, riefe niemand mehr `onGeschlossen`, und der Esc-Riegel
  // des Panels bliebe fuer dessen Lebensdauer stehen.
  const blattOffenRef = useRef(false)
  useEffect(() => { blattOffenRef.current = blattOffen }, [blattOffen])
  useEffect(() => () => {
    if (blattOffenRef.current) {
      blattOffenRef.current = false
      geschlossenCb.current?.()
    }
  }, [])

  useEffect(() => {
    if (!blattOffen) return undefined
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      // Der Esc-Riegel in BEIDE Richtungen: das Blatt schliesst, die Suche
      // bleibt offen. Der Panel-Zuhoerer prueft dieselbe Lage ueber
      // `onGeoeffnet`/`onGeschlossen` und schweigt, solange das Blatt offen ist.
      //
      // 🔴 R15-W6 (Nachbesserung 1): hier stand `e.stopPropagation()`. Der
      // Panel-Zuhoerer sitzt am SELBEN Knoten (`document`); `stopPropagation`
      // haelt keine Geschwister-Listener auf, nur `stopImmediatePropagation`
      // taete es — und das haengt an der Registrierungsreihenfolge. Der Riegel
      // traegt ohnehin ueber den ZUSTAND (`mobileBereichsBlattOffen` im Panel),
      // deshalb ist die wirkungslose Zeile entfernt.
      offenRef.current = false
      setBlattOffen(false)
      geschlossenCb.current?.()
    }
    // Tippen daneben: Blatt zu, Suche offen, Fokus ins Feld. Der Ausloeser
    // selbst ist ausgenommen — sein Klick TOGGLET das Blatt (s. oben).
    const onPointer = (e) => {
      const blatt = blattRef.current
      const knopf = wurzelRef.current
      if (blatt && blatt.contains(e.target)) return
      if (knopf && knopf.contains(e.target)) return
      // 🔴 M3.9d (Nachbesserung 1): „All filters" in der Bereichsleiste ist der
      // AUSLOESER und TOGGLET das Blatt — er zaehlt nicht als „ausserhalb".
      // Ohne diese Ausnahme schloss sein `pointerdown` das Blatt, der darauf
      // folgende `click` las `false` und oeffnete es wieder: der zweite Klick
      // tat nichts (gemessen, Kette suche2c). Die Marke setzt der Aufrufer am
      // Chip (`SucheBereichChips`), nicht diese Komponente.
      if (e.target?.closest?.('[data-blatt-ausgenommen]')) return
      // 🔴 Suche mobil v2 Stufe 1, Nachbesserung 1 (2026-10-31) — `preventDefault`
      // aus DEMSELBEN Grund wie am Panel-Backdrop (`SidebarAndHeaderBar.jsx`,
      // `onPointerDown` der Abdunklung): der Fokus wird auf `pointerdown`
      // gesetzt, und die Standardaktion des darauf folgenden `mousedown` nimmt
      // ihn wieder weg, wenn das Ziel nicht fokussierbar ist. Seit v2 ist der
      // Tipp daneben die RANDLOSE Trefferliste (sie traegt `pointerEvents:
      // 'auto'` und ist damit das Ziel) statt der Abdunklung — dieser Weg lief
      // bis dahin nie ueber den Panel-Zweig, und `M3.9b` war rot: „Blatt zu,
      // Fokus zurueck aufs Feld" ist ein bestehender Vertrag.
      e.preventDefault()
      offenRef.current = false
      setBlattOffen(false)
      geschlossenCb.current?.()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [blattOffen])

  // 🔴 R16-W6 (Nachbesserung 1) — das Tastaturmuster der Listbox. Das WAI-ARIA-
  // APG-Muster „listbox popup" verlangt: Fokus beim Oeffnen in die Liste,
  // Pfeil hoch/runter, Home/End, Enter/Leertaste waehlt, Escape schliesst.
  // Ohne das verspricht `role="listbox"` mehr, als das Widget haelt.
  //
  // 🔴 Gesperrte Zeilen werden NICHT uebersprungen. Sie sind echte `<button>`s
  // mit `aria-disabled` und dem Grund im zugänglichen Namen; ein Sprung darueber
  // machte diesen Grund fuer Tastaturnutzer unerreichbar. Enter/Leertaste
  // loesen den `onClick` aus, der im Riegel zurueckkehrt — die Zeile bleibt
  // also stehen, ohne zu waehlen.
  useEffect(() => {
    if (!blattOffen) return
    // Beim Oeffnen den Fokus auf die GEWAEHLTE Zeile (sonst die erste). Das
    // Blatt wird per Portal gerendert; der Effekt laeuft nach dem Commit.
    //
    // 🔴 R16-W14 (Nachbesserung 2): der Effekt laeuft AUCH, wenn die Liste
    // nach dem Oeffnen eintrifft (`bereiche`/`unbekannt` aendern sich). Dann
    // verschwindet die Zeile „unbekannter Bereich" unter dem Fokus, und der
    // Fokus fiele auf `<body>`. Er geht auf die gewaehlte bzw. erste Option.
    //
    // 🔴 R16-W18 / R15-W5 (Nachbesserung 3): der Effekt hat ZWEI Aufgaben, und
    // die Grenze zwischen ihnen ist der Punkt.
    //
    //   (a) Steht der Fokus IN der Liste, wird er nicht gestohlen — aber der
    //       Roving-Index wird auf die fokussierte Zeile NACHGEZOGEN. Sonst
    //       verschoeben sich bei einer nachgeladenen Liste alle Indizes, und
    //       danach truege KEINE Option `tabIndex=0`.
    //   (b) Fokus GEHOLT wird nur, wenn er VERSCHWUNDEN ist (`<body>` oder ein
    //       Knoten ausserhalb des Dokuments) — oder beim ERSTEN Oeffnen. Steht
    //       er auf einem lebenden Element ausserhalb (Ausloeser,
    //       Schliessen-Knopf), bleibt er dort: jede nachgeladene Antwort risse
    //       den Nutzer sonst aus seinem Element.
    const el = blattRef.current
    if (!el) return
    const erstOeffnen = erstOeffnenRef.current
    erstOeffnenRef.current = false
    const optionen = Array.from(el.querySelectorAll('button[role="option"]'))
    const aktiv = document.activeElement
    if (aktiv && el.contains(aktiv)) {
      const i = optionen.indexOf(aktiv)
      if (i >= 0) setBlattFokus(i)
      return
    }
    const fokusWeg = !aktiv || aktiv === document.body || !document.contains(aktiv)
    if (!erstOeffnen && !fokusWeg) return
    const ziel = el.querySelector('button[role="option"][aria-selected="true"]')
      || optionen[0]
    if (!ziel) return
    ziel.focus()
    const index = optionen.indexOf(ziel)
    if (index >= 0) setBlattFokus(index)
  }, [blattOffen, bereiche, wert])

  const beiTasteImBlatt = (e) => {
    const el = blattRef.current
    if (!el) return
    const optionen = Array.from(el.querySelectorAll('button[role="option"]'))
    if (!optionen.length) return
    const i = optionen.indexOf(document.activeElement)
    const springe = (n) => {
      e.preventDefault()
      const ziel = (n + optionen.length) % optionen.length
      optionen[ziel].focus()
      // 🔴 R16-W5: der Tab-Stopp wandert mit dem Fokus.
      setBlattFokus(ziel)
    }
    if (e.key === 'ArrowDown') springe(i < 0 ? 0 : i + 1)
    else if (e.key === 'ArrowUp') springe(i < 0 ? optionen.length - 1 : i - 1)
    else if (e.key === 'Home') springe(0)
    else if (e.key === 'End') springe(optionen.length - 1)
  }

  // 🔴 **Ein gewaehlter Bereich, den die Liste (noch) nicht kennt, heisst NICHT
  // „All".** Zwei Lagen erzeugen das: die Liste laedt noch, oder sie ist
  // ausgefallen. In beiden stuende „All" in der Pille, waehrend die Abfrage
  // weiterhin `types=[kind]` schickt — die Anzeige behauptete das Gegenteil
  // dessen, was gemessen wird. Deshalb faellt die Beschriftung auf die rohe
  // Kennung zurueck, und das Menue traegt sie als eigene Zeile (s. u.).
  const unbekannt = Boolean(wert) && !bereiche.some((b) => b.kind === wert)

  // 🔴 **Der wirksame Bereich ueberstimmt die Auswahl** (R16-B2). Er kommt vom
  // Server, weil der Parser dort sitzt (B6) — hier wird NICHTS geparst.
  //
  // 🔴 `value` bleibt die AUSWAHL, nur die Anzeige folgt dem wirksamen Bereich.
  // Der Unterschied ist der ganze Punkt: die Auswahl ist der Zustand fuer den
  // naechsten Freitext (und die hervorgehobene Zeile im Menue), die Anzeige ist
  // das, wonach JETZT gefiltert wird. Waere `value` der wirksame Bereich, waere
  // nach dem Leeren des Textes die Auswahl stillschweigend eine andere.
  const ueberstimmt = Boolean(wirksam) && wirksam !== wert
  const anzeige = ueberstimmt ? wirksam : wert
  // 🔴 Der Kurzweg ist der BELEG der Ueberstimmung — und er haengt am
  // SERVER-SIGNAL, nicht am Unterschied (R16-W1). `ueberstimmt` allein hat zwei
  // Ursachen: die Syntax hat gegriffen, oder die Messung ist veraltet, weil die
  // AUSWAHL gerade umgestellt wurde. Im zweiten Fall zeigte der Zusatz eine
  // Syntax, die nie getippt wurde. `syntaxWirksam` ist genau „der Server hat
  // meinen Text angetastet" (`X-Search-Q` wird nur dann gesetzt).
  const kurzweg = ueberstimmt && syntaxWirksam ? kurzwegVon(wirksam, bereiche) : null

  const beschriftung = (kind) => bereichsEtikett(kind, bereiche)

  // 🔴 R16-W5 — die Reihenfolge der Optionen im Blatt, EINE Quelle fuer das
  // Rendering (unten) und den Roving-Index. „All", die unbekannte gewaehlte
  // Art (falls die Liste sie nicht kennt) und die Server-Liste.
  const blattWerte = [
    ALLE_BEREICHE,
    ...(unbekannt ? [wert] : []),
    ...bereiche.map((b) => b.kind),
  ]
  // Der Tab-Stopp beim Oeffnen: die GEWAEHLTE Zeile, sonst die erste.
  const blattStart = Math.max(0, blattWerte.indexOf(anzeige))

  // 🔴 Suche mobil v2 Stufe 2 — das Blatt OHNE Ausloeser wird von AUSSEN
  // geoeffnet (der Chip „All filters"). Die Nebenbedingungen des Oeffnens
  // (Tab-Stopp auf der gewaehlten Zeile, Marke fuer den Fokus-Effekt, frische
  // Liste, `onGeoeffnet`) haengen sonst nur am Ausloeser-Klick und liefen fuer
  // diesen Weg nie. Der Ref macht daraus eine FLANKE: nur der Wechsel
  // geschlossen → offen loest aus.
  const warOffenRef = useRef(false)
  useEffect(() => {
    if (!ohneAusloeser) return
    if (blattOffen && !warOffenRef.current) {
      warOffenRef.current = true
      setBlattFokus(blattStart)
      erstOeffnenRef.current = true
      beimOeffnen()
    } else if (!blattOffen) {
      warOffenRef.current = false
    }
    // `beimOeffnen`/`blattStart` sind je Render neu, aber der Ref-Riegel macht
    // den Effekt idempotent; sie in die Liste zu nehmen liefe ihn bei jedem
    // Render — dieselbe Bauform wie `geschlossenCb` oben.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blattOffen, ohneAusloeser])

  /** Der sichtbare Inhalt der Pille — EINMAL definiert, von der Desktop-
   *  Klappliste UND dem mobilen Knopf geteilt (§B2.7: „demselben
   *  `renderValue`-Inhalt"). Der Kurzweg ist der Beleg der Ueberstimmung; er
   *  traegt `color: 'inherit'` und KEINE Abschwaechung (R16-B1: `opacity: 0.75`
   *  fiel hell fokussiert auf 3,71:1, das Etikett daneben hat 6,89:1). */
  const anzeigeInhalt = () => (
    <Box
      component="span"
      // Messflaeche der Breitenzusage (S.6, `tests/search/arten.spec.ts`):
      // `scrollWidth > clientWidth` belegt, dass das Etikett GEKUERZT wird und
      // nicht in die Pille hineinpasst — ohne diesen Beleg waere „Breite
      // gleich" auch bei einem Etikett zu sehen, das nie haette wachsen koennen
      // (Regel 17 §3b).
      data-testid="bereich-etikett"
      sx={{
        fontSize: FS.sm,
        whiteSpace: 'nowrap',
        // Das Etikett kuerzt, es schiebt nicht (S, Nachbesserung 4): die Pille
        // traegt ihre Breite selbst.
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {beschriftung(anzeige)}
      {kurzweg && (
        <Box
          component="span"
          data-testid="bereich-kurzweg"
          // R16-B1: KEINE Palettenfarbe — die Pille sitzt auf einer Flaeche,
          // deren Polaritaet dem Modus folgt; sie traegt ihren Vordergrund.
          sx={{ color: 'inherit', fontSize: FS.xs }}
        >
          {` (${kurzweg}:)`}
        </Box>
      )}
    </Box>
  )

  // ── M3b §B2.7 — die MOBILE Form: ein echter Knopf und ein Blatt ────────────
  if (istMobil) {
    // Das Blatt ist ein DOM-Kind der Saeule, NACH der Karte (§B2.8) — und es
    // liegt NICHT in der Kapsel: die traegt `touch-action: none`, das jede
    // Wischgeste ihrer Nachfahren sperrte. Deshalb ein Portal in die
    // Panelwurzel; ohne Panel (Einzelprobe) bleibt es an seiner Stelle.
    const panelWurzel = wurzelRef.current?.closest?.('[data-testid="mobil-suche-panel"]')
    const blatt = blattOffen ? (
      <Box
        ref={blattRef}
        data-testid="bereich-blatt"
        sx={{
          // Es VERTRITT die Trefferkarte an ihrer Stelle (§B2.7): Unterkante
          // 12 px ueber der Kapsel, seitlich derselbe Abstand, Obergrenze die
          // Saeulenoberkante — darunter scrollt es selbst. KEIN eigener
          // `zIndex`: als Kind der Saeule entscheidet die DOM-Reihenfolge.
          position: 'absolute',
          left: 'var(--suche-abstand)',
          right: 'var(--suche-abstand)',
          // 🔴 Suche mobil v2 — der ANKER (Plan §10 Stufe 2, V2.9). Das Blatt
          // oeffnet UNTER der Bereichsleiste; `blattObenPx` ist ihre Unterkante
          // samt Polster. Den `unten`-Zweig gibt es nicht mehr (Plan-Nachtrag
          // N7 a): er trug das eingefrorene Kapselmass `SUCHE_BLATT_UNTEN_PX`
          // fuer eine Geometrie, die seit v2 niemand mehr baut, und kein
          // Aufrufer setzte ihn.
          top: `${blattObenPx}px`,
          maxHeight: `calc(100% - ${blattObenPx}px - ${SUCHE_ABSTAND_PX}px)`,
          // 🔴 R15-W2 / R16-W17 (Nachbesserung 3): die Schale ist der
          // GESCHWISTER-Container von Hinweiszeile und Listbox — die Scrollachse
          // bleibt HIER, damit `M3.8b` (das Blatt scrollt selbst) unveraendert
          // gilt; die Hinweiszeile scrollt wie zuvor mit.
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          pointerEvents: 'auto',
          ...MOBILE_SEARCH_GLASS_SX,
          // 🔴 R16-W5 (Nachbesserung 1): derselbe Radius wie die Trefferkarte,
          // als Token — vorher stand hier ein zweites `'20px'`.
          borderRadius: SUCHE_KARTE_RADIUS,
          // 🔴 R16-W11 / R15-W4 (Nachbesserung 2): KEIN Schatten. Der Satz
          // „wird nicht geklippt" galt nur bei kurzer Liste — bei `maxHeight`
          // ist die Blattoberkante die Saeulenoberkante, und der nach oben
          // gerichtete Schatten liegt im `overflow: hidden` der Saeule
          // (seitlich immer teilweise). Die flache Kante mit der Lichtkante
          // aus `var(--glass-border)` ist dieselbe flache Form.
          border: '1px solid var(--glass-border)',
          p: 0.5,
        }}
      >
        {/* Die Hinweiszeile bei wirksamer Syntax: NICHT waehlbar, gleicher
            Wortlaut wie im Desktop-Menue, zweiter Riegel bleibt `onChange`.
            🔴 R16-W12 (Nachbesserung 2): sie ist KEINE Option mehr. Als
            `role="option"` ohne `<button>` war sie eine deklarierte Option, die
            die Pfeilnavigation (`button[role="option"]`) überspringt — ein
            Versprechen, das das Widget nicht hielt. `role="note"` ist die
            Hinweis-Semantik; die Pfeilnavigation ist unberührt, weil sie nur
            Knoepfe liest.
            🔴 R15-W2 / R16-W17 (Nachbesserung 3): sie ist auch kein KIND der
            Listbox mehr. ARIA laesst dort nur `option`/`group` zu; ein `note`
            faellt je nach AT aus dem Listbox-Baum. Sie steht als GESCHWISTER
            DAVOR, und die Listbox zeigt per `aria-describedby` auf sie. */}
        {syntaxWirksam && (
          <Box
            role="note"
            id={hinweisId}
            data-testid="bereich-syntax-hinweis"
            sx={{
              fontSize: FS.xs, color: 'text.secondary', px: 2, py: 1,
              cursor: 'default', whiteSpace: 'normal',
            }}
          >
            {SYNTAX_HINWEIS_TEXT}
          </Box>
        )}
        <Box
          role="listbox"
          aria-label="Search area"
          aria-describedby={syntaxWirksam ? hinweisId : undefined}
          onKeyDown={beiTasteImBlatt}
        >
        {/* „All" */}
        <Box
          component="button"
          type="button"
          role="option"
          data-value={ALLE_BEREICHE}
          aria-selected={anzeige === ALLE_BEREICHE}
          // 🔴 R16-W5 — Roving-Tabindex: nur der Tab-Stopp ist erreichbar.
          tabIndex={blattFokus === 0 ? 0 : -1}
          onClick={() => { onChange?.(ALLE_BEREICHE); schliesseBlatt() }}
          sx={BLATT_ZEILE_SX}
        >
          <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>All</Box>
          {anzeige === ALLE_BEREICHE && <CheckIcon sx={{ ml: 1, fontSize: FS.xl }} />}
        </Box>
        {/* Die gewaehlte Art auch dann, wenn die Liste sie nicht kennt. */}
        {unbekannt && (
          <Box
            component="button"
            type="button"
            role="option"
            data-value={wert}
            aria-selected={anzeige === wert}
            tabIndex={blattFokus === 1 ? 0 : -1}
            onClick={() => { onChange?.(wert); schliesseBlatt() }}
            sx={BLATT_ZEILE_SX}
          >
            <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>{wert}</Box>
            {anzeige === wert && <CheckIcon sx={{ ml: 1, fontSize: FS.xl }} />}
          </Box>
        )}
        {bereiche.map((b, i) => {
          const abgeschaltet = b.aktiv === false
          const index = (unbekannt ? 2 : 1) + i
          return (
            <Box
              key={b.kind}
              component="button"
              type="button"
              role="option"
              data-value={b.kind}
              aria-selected={anzeige === b.kind}
              tabIndex={blattFokus === index ? 0 : -1}
              // 🔴 Kein echtes `disabled`: im Blatt sind die Zeilen eigene
              // Knoepfe, kein MUI-`MenuItem`; `aria-disabled` haelt die Zeile
              // lesbar, ein echtes `disabled` naehme `aria-label` in manchen
              // Screenreadern aus der Vorlesung. Der `onChange`-Riegel bleibt.
              aria-disabled={abgeschaltet ? 'true' : undefined}
              aria-label={abgeschaltet ? `${b.label} — ${ABGESCHALTET}` : undefined}
              onClick={() => {
                if (bereichIstAbgeschaltet(b.kind)) return
                onChange?.(b.kind)
                schliesseBlatt()
              }}
              // 🔴 R16-W9 (Nachbesserung 1): eine gesperrte Zeile traegt NICHT
              // den Zeiger der waehlbaren. Sie sieht sonst anklickbar aus,
              // obwohl der Klick im Riegel zurueckkehrt — das Desktop-Menue
              // zeigt fuer `disabled` `cursor: default`.
              sx={abgeschaltet ? { ...BLATT_ZEILE_SX, cursor: 'default' } : BLATT_ZEILE_SX}
            >
              <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>{b.label}</Box>
              {b.aliases?.[0] && (
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{ ml: 1, color: 'text.secondary', fontSize: FS.xs }}
                >
                  {b.aliases[0]}:
                </Box>
              )}
              {abgeschaltet && (
                <Box
                  component="span"
                  data-testid="bereich-gesperrt-grund"
                  sx={{ ml: 1, color: 'text.secondary', fontSize: FS.xs }}
                >
                  {ABGESCHALTET}
                </Box>
              )}
              {anzeige === b.kind && <CheckIcon sx={{ ml: 1, fontSize: FS.xl }} />}
            </Box>
          )
        })}
        </Box>
      </Box>
    ) : null

    // 🔴 Stufe 2 — ohne Ausloeser bleibt nur das Blatt. Der Knopf in der
    // Feldzeile ist entfallen; geoeffnet wird es ueber „All filters" in der
    // Bereichsleiste (Plan §9.1, W14).
    if (ohneAusloeser) {
      // Der Ref haengt an einem `display: contents`-Anker: `panelWurzel` wird
      // aus ihm abgeleitet (Portal in die Saeule, §B2.8), und ohne ihn faende
      // das Blatt seinen Bezugsrahmen nicht.
      return (
        <Box ref={wurzelRef} sx={{ display: 'contents' }}>
          {blatt && (panelWurzel ? createPortal(blatt, panelWurzel) : blatt)}
        </Box>
      )
    }

    return (
      <>
        <Box
          component="button"
          type="button"
          ref={wurzelRef}
          data-testid="bereich-wahl"
          // 🔴 R16-W2 (Nachbesserung 1): der aktuelle Bereich gehoert in den
          // zugaenglichen Namen. Ein blosses „Search area" ERSETZT den sichtbaren
          // Inhalt (Accessible-Name-Computation) — ein Screenreader hoerte nie,
          // welcher Bereich gilt, obwohl der Knopf ihn anzeigt (WCAG 2.5.3).
          aria-label={`Search area: ${beschriftung(anzeige)}`}
          aria-haspopup="listbox"
          aria-expanded={blattOffen}
          onClick={beimOeffnenMobil}
          sx={{
            flexShrink: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            background: 'none',
            border: 'none',
            p: 0,
            font: 'inherit',
            color: 'inherit',
            cursor: 'pointer',
            textAlign: 'left',
            ...SUCHBEREICH_MIN_HEIGHT_SX,
          }}
        >
          {anzeigeInhalt()}
        </Box>
        {blatt && (panelWurzel ? createPortal(blatt, panelWurzel) : blatt)}
      </>
    )
  }

  return (
    <Select
      ref={wurzelRef}
      value={wert || ALLE_BEREICHE}
      // 🔴 Der zweite Riegel der Hinweiszeile (s. `SYNTAX_HINWEIS`): MUI klont
      // jedes Kind zur Option und feuert beim Klick darauf `onChange`, auch bei
      // `disabled`. Die Kennung der Hinweiszeile ist keine Sicht und wird
      // deshalb hier verworfen — die Auswahl aendert sich nicht.
      onChange={(e) => {
        if (e.target.value === SYNTAX_HINWEIS) return
        // 🔴 Der zweite, TRAGENDE Riegel der gesperrten Zeile (B2) — dieselbe
        // Bauform wie bei `SYNTAX_HINWEIS` und aus demselben Grund: MUI klont
        // jedes Kind zur Option und setzt einen EIGENEN `onClick`; `disabled`
        // allein hat dort schon einmal nicht gehalten (gemessen 2026-10-15,
        // Probe „W3 — nicht waehlbare Hinweiszeile" war mit `disabled` allein
        // rot). Eine abgeschaltete Art ist damit nicht waehlbar, auch wenn der
        // Klick durchkommt — und der Grund steht sichtbar in der Zeile.
        if (bereichIstAbgeschaltet(e.target.value)) return
        onChange?.(e.target.value)
      }}
      onOpen={beimOeffnen}
      // 🔴 `onGeschlossen` laeuft NACHDEM das Menue geschlossen ist (MUI's
      // `onExited` der Grow-Transition, ueber `TransitionProps` — in MUI 7 der
      // Weg zu `slotProps.transition`). ⚠️ Die Fundstelle in `Menu.js` ist
      // NICHT nachgelesen (`node_modules` liegt ausserhalb des Arbeitsbaums,
      // der Zugriff wurde abgelehnt); die Mechanik ist am ausgelieferten
      // Verhalten gemessen (s. o. und R15-W3).
      //
      // Der Aufrufer braucht das, um den FOKUS zurueckzuholen (Owner-Befund
      // 2026-10-16): MUI gibt ihn beim Schliessen an den AUSLOESER zurueck —
      // den Bereichsknopf —, nicht an das Suchfeld. Ein `focus()` im `onChange`
      // derselben Runde wuerde davon ueberschrieben, weil die Rueckgabe
      // spaeter faellt. Am `onExited` haengt sie NACH ihr.
      MenuProps={menuProps}
      variant="standard"
      disableUnderline
      // 🔴 **`displayEmpty` ist Pflicht, nicht Kosmetik.** Ohne es rendert MUI
      // `renderValue` bei einem LEEREN Wert gar nicht — und „All" IST der leere
      // Wert. Die Pille stand damit als leerer Kasten da, sobald „All" gewaehlt
      // war (also im Auslieferungszustand). Gefunden von der Jest-Probe
      // „ohne Server-Antwort bleibt All", nicht von der Lektuere.
      displayEmpty
      data-testid="bereich-wahl"
      inputProps={{ 'aria-label': 'Search area' }}
      // 🔴 Das Argument `v` wird ABSICHTLICH nicht gelesen: es ist die Auswahl
      // (`value`), angezeigt wird der wirksame Bereich. Ein `v` hier waere genau
      // der Fehler, den R16-B2 beschreibt.
      renderValue={anzeigeInhalt}
      sx={[
        {
          // 🔴 **Die Pille darf nicht am Etikett wachsen** (S, Nachbesserung 4,
          // 2026-10-18). `kopf-suchkasten` hat eine FESTE Breite (`65ch` offen,
          // `35ch` zu); ein Flex-Kind mit `min-width: auto` kann sie trotzdem
          // aufblaehen, sobald sein Min-Content groesser wird — genau das war
          // die Sorge hinter dem 32-px-Sprung. `minWidth: 0` nimmt dem Select
          // die automatische Untergrenze, und das Etikett kuerzt stattdessen
          // (Ellipse im `renderValue`), statt die Pille zu verbreitern.
          //
          // `flexShrink: 1` statt `0`: ohne Schrumpf bliebe das Select auf
          // seiner Min-Content-Breite stehen und liefe aus dem Kasten heraus —
          // die Pille bliebe zwar fest, das Etikett wuerde aber ABGESCHNITTEN
          // statt mit Ellipse gekuerzt, und es schoebe sich ueber die Eingabe.
          flexShrink: 1,
          minWidth: 0,
          color: 'inherit',
          // 🔴 **Das verdreifachte `&` ist kein Zierrat — es ist die Reparatur,
          // und sie ist ZWEIMAL noetig gewesen.** MUI setzt auf genau diesen
          // Knoten selbst `min-height: 1.4375em` (`SelectInput.js`:
          // `&.MuiSelect-select`, ZWEI Klassen) und `padding-right: 24px`
          // (`NativeSelectInput.js:69-72`: `'&&&'`, DREI Klassen).
          //
          //   1. Gegen das `min-height` genuegte `&&` (`.css.css .MuiSelect-select`
          //      = drei Token gegen zwei). Gemessen im Browser (B.7/B.8,
          //      2026-10-15): die Flaeche war **20,125 px** hoch (14 px Schrift ×
          //      1,4375) statt der geforderten 24, im Mobil-Panel **23 px**
          //      (16 px × 1,4375) statt 44 — unser `minHeight` griff gar nicht.
          //   2. Gegen das `padding-right` genuegte `&&` NICHT: es sind ebenfalls
          //      drei Token, und MUI's Regel kommt spaeter. Gemessen im Browser
          //      (R15 W-3, 2026-10-16): das `padding-right` war **24 px**, unser
          //      `pr: 2.5` (20 px) griff NIE. Der Abstand ist
          //      `padding-right − right − Pfeilbreite`, und die letzten beiden
          //      heben sich in beiden moeglichen Lesarten auf (4 + 20 bzw. 0 + 24)
          //      — er war also **0**, und der Pfeil BERUEHRTE das Etikett
          //      (`luecke = −1 px` bei „Competitor", „Purchase Order", „Inbox
          //      mail"). `pr` ist ersatzlos weg; den Platz traegt
          //      `SUCHBEREICH_PFEILRAUM_SX`.
          //
          // `&&&` ergibt `.css.css.css .MuiSelect-select` — vier Token, damit
          // reihenfolgeunabhaengig gegen BEIDE MUI-Regeln. Auf der WURZEL haette
          // derselbe Wert nichts geaendert: die Wurzel ist
          // `inline-flex`/`align-items: center`, das Kind waechst nicht mit, und
          // `boundingBox()` misst das Kind.
          '&&& .MuiSelect-select': {
            py: 0,
            pl: 0,
            display: 'flex',
            alignItems: 'center',
            // Etikett kuerzen statt Pille verbreitern (s. `minWidth: 0` oben).
            minWidth: 0,
            overflow: 'hidden',
            // Trefferflaeche, zwei Achsen — benannt in `theme/tokens.js`
            // (`SUCHBEREICH_MIN_HEIGHT_SX`, R16-W4): 44 px auf grobem Zeiger
            // ODER unterhalb `sm`, sonst der WCAG-2.5.8-Boden 24.
            // 🔴 Eine ECHTE Hoehe, keine unsichtbare Ueberlagerung:
            // `boundingBox()` und der Haus-Waechter
            // (`frontend/tests/design/steuerhoehe-grober-zeiger.spec.ts`) messen
            // die gerenderte Flaeche, ein `::after` saehen sie nicht.
            ...SUCHBEREICH_MIN_HEIGHT_SX,
            // Der Pfeil-Lauf: Pfeilbreite + 4–12 px Luft. Ohne ihn beruehrten
            // sich Etikett und Pfeil (Befund oben). Gilt fuer Kopf UND
            // Mobil-Panel — die Deklaration haengt nicht an einer Bauform.
            ...SUCHBEREICH_PFEILRAUM_SX,
          },
          '& .MuiSelect-icon': { color: 'inherit', opacity: 0.7, right: 4 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* 🔴 R16-W3: Ist die getippte Syntax wirksam, bewirkt eine Auswahl hier
          nichts Sichtbares — die Pille folgt weiter dem wirksamen Bereich. Ohne
          diese Zeile klickt jemand „Product", das Menue schliesst, und die Pille
          sagt weiter `Purchase Order (po:)`: zwei sichtbare Flaechen, zwei
          Aussagen, kein Wort darueber warum.

          🔴 Sie ist eine HINWEISZEILE, keine Auswahl: `disabled` gibt ihr
          `aria-disabled` (MUI setzt es selbst) und nimmt sie aus der
          Tastaturfuehrung; ein `value`, das keine Kennung der Sicht sein kann,
          haelt sie aus `areEqualValues` heraus. Die Auswahl aendert sich nicht —
          der Rueckweg ist „Text leeren", und das steht im Changelog.

          🔴 Der Wortlaut nennt URSACHE **und** RUECKWEG (R16-W3, Orchestrator-
          Entscheid 2026-10-15): „Your typed prefix sets the area" nannte nur die
          Ursache — nicht, dass eine Auswahl hier nichts bewirkt, und nicht, wie
          man hierher zurueckkommt. Die Mikrocopy-Regel des Hauses verlangt beides
          (Ursache + naechster Schritt).

          🔴 Die Deckkraft kommt aus `LESBARE_SPERRE_SX` (s. o. — die
          Spezifitaetsbegruendung steht dort, EINE Stelle fuer beide Zeilen,
          R16-B2/B3). Ohne sie stuende der Hinweis — die EINZIGE Erklaerung dafuer,
          dass eine Auswahl hier nichts bewirkt — auf MUI's 0,38, also ≈ 1,7:1
          (text.secondary mal 0,38 auf `background.paper`), und verloere mit
          seiner Aussage die ganze Auflage R16-W3. Vorbilder im Haus:
          `SidebarAndHeaderBar.jsx:468`, `FilterSheet.jsx:532`,
          `__tests__/SidebarAbschnittsKopf.modus.test.jsx:51`. */}
      {syntaxWirksam && (
        <MenuItem
          disabled
          value={SYNTAX_HINWEIS}
          data-testid="bereich-syntax-hinweis"
          sx={{
            fontSize: FS.xs,
            ...LESBARE_SPERRE_SX,
            // 🔴 Der Hinweis darf UMBRECHEN (R16-C3, gemessen 2026-10-16).
            //
            // MUI's `MenuItem` traegt `white-space: nowrap` auf der Wurzel —
            // dieselbe Regel, die auch `min-height: 48px` setzt (bis 600 px; die
            // Jest-Probe „C3" kalibriert das am ausgelieferten CSSOM, statt es
            // zu glauben). Der Satz ist mit 69 Zeichen 2,3-mal so lang wie der
            // abgeloeste, und das Menue ist auf `100vw − 32px` gedeckelt: bei
            // 1440 px steht er einzeilig (Menue 399 px), bei 390 px endete er
            // mitten im Satz — „…clear the text to choose", das „here." fehlte,
            // ohne Ellipse (Foto `hinweis_390`).
            //
            // 🔴 **Warum `'&.MuiMenuItem-root'` und nicht `whiteSpace` daneben —
            // und hier stand zuerst die FALSCHE Begruendung.** Geraten war:
            // „ein blankes `whiteSpace` erzeugt eine Regel mit EINER Klasse, dann
            // entscheidet die Reihenfolge". **Gemessen** (Jest, CSSOM) stimmt das
            // nicht: ein blankes `whiteSpace: 'normal'` wird in MUI's EIGENE
            // MenuItem-Regel gefaltet und ERSETZT dort das `nowrap` — der
            // Mutant wirkte also, und die Probe fiel an ihrer Kalibrierzeile,
            // nicht an ihrer Zusage.
            //
            // Was trotzdem fuer die genestete Form spricht, ist die Sichtbarkeit:
            // so bleibt MUI's `nowrap`-Regel stehen und wird per Spezifitaet
            // geschlagen (zwei Klassen gegen eine), statt still ersetzt zu werden.
            // Die Probe kann damit BEIDE Haelften zusichern — dass es die fremde
            // Vorgabe gibt und dass unsere gewinnt. Und eine kuenftige MUI-Fassung,
            // die ihre Deklaration in eine genestete Regel zieht, faellt damit
            // auf, statt stumm zu gewinnen.
            '&.MuiMenuItem-root': { whiteSpace: 'normal' },
            color: 'text.secondary',
            cursor: 'default',
          }}
        >
          {SYNTAX_HINWEIS_TEXT}
        </MenuItem>
      )}
      <MenuItem value={ALLE_BEREICHE} sx={MENUEZEILE_SX}>
        All
      </MenuItem>
      {/* 🔴 Die gewaehlte Art auch dann als Zeile, wenn die Liste sie nicht
          (mehr) kennt. Ohne sie warnt MUI „out-of-range value" und der Wert
          stuende ohne Zeile im Feld — ein Ausfall saehe aus wie eine leere
          Auswahl. Sie traegt die rohe Kennung: sie ist die einzige Wahrheit,
          die in dieser Lage zur Verfuegung steht. */}
      {unbekannt && (
        <MenuItem value={wert} sx={MENUEZEILE_SX}>
          {wert}
        </MenuItem>
      )}
      {bereiche.map((b) => {
        // 🔴 Eine abgeschaltete Art wird GESPERRT und BENANNT, nicht
        // ausgeblendet (B2, Orchestrator-Entscheid). Ausblenden waere die
        // naheliegende und falsche Abhilfe: die Art verschwaende aus dem Menue,
        // und niemand erfuehre, dass es sie gibt und wo sie wieder einzuschalten
        // ist. Die Sperre sagt beides.
        const abgeschaltet = b.aktiv === false
        return (
          <MenuItem
            key={b.kind}
            value={b.kind}
            disabled={abgeschaltet}
            // 🔴 Der Grund im ZUGAENGLICHEN NAMEN (Auflage 3) — und zwar als
            // EINE Zeichenkette, nicht als Zusammenbau aus Textknoten. Gemessen
            // (Mutation b, 2026-10-16): ohne diese Zeile lautete der Name
            // „Blog Topic Off in Admin settings" — das Trennzeichen fehlt, und
            // wie die Namenberechnung Textknoten aneinanderfuegt, haengt am
            // Werkzeug (R16-W2 hat beim Kurzweg daneben „Purchase Orderpo:"
            // gemessen, ohne Trennzeichen). Mit `aria-label` steht der Name
            // fest; die sichtbare Beschriftung bleibt darin enthalten
            // (WCAG 2.5.3 „Label in Name").
            aria-label={abgeschaltet ? `${b.label} — ${ABGESCHALTET}` : undefined}
            // 🔴 Die Hausform der lesbaren Sperre (R16-B3, s. `LESBARE_SPERRE_SX`):
            // der Grund ist der ZWECK der Zeile — „gesperrt UND sichtbar benannt" —
            // und darf nicht auf MUI's 0,38 verblassen. Textanwesenheit ist von der
            // Deckkraft unabhaengig, deshalb blieb der Befund bei jeder Zusicherung
            // gruen.
            sx={abgeschaltet ? { ...MENUEZEILE_SX, ...LESBARE_SPERRE_SX } : MENUEZEILE_SX}
          >
            {b.label}
            {/* Der Kurzweg, den der Parser kennt — aus derselben Antwort.
                🔴 `aria-hidden` (R16-W2): der zugängliche Name der Menuezeile war
                „Purchase Orderpo:", weil `ml: 1` Rand und kein Textzeichen ist und
                beide Woerter im selben Namen landeten. Der Hinweis ist eine
                ANZEIGE-Hilfe; die Zeile heisst „Purchase Order". */}
            {b.aliases?.[0] && (
              <Box
                component="span"
                aria-hidden="true"
                // 🔴 `text.secondary`, nicht `text.disabled` (R16-W1): der Hinweis
                // ist inhaltlich — er ist die einzige Entdeckung der Syntax —, kein
                // ausgegrauter Zustand. `text.disabled` ergab bei `FS.xs` rund
                // 2,6:1 gegen die geforderten 4,5:1.
                sx={{ ml: 1, color: 'text.secondary', fontSize: FS.xs }}
              >
                {b.aliases[0]}:
              </Box>
            )}
            {abgeschaltet && (
              // 🔴 KEIN `aria-hidden` — anders als der Kurzweg daneben: hier ist
              // der Zusatz die ganze Aussage (s. `ABGESCHALTET`). Farbe wie der
              // Kurzweg, damit beide Zusaetze dieselbe Rolle haben.
              <Box
                component="span"
                data-testid="bereich-gesperrt-grund"
                sx={{ ml: 1, color: 'text.secondary', fontSize: FS.xs }}
              >
                {ABGESCHALTET}
              </Box>
            )}
          </MenuItem>
        )
      })}
    </Select>
  )
}
